import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import CustomSelect from '../../components/common/CustomSelect';
import { auditLogsApi } from '../../services/api';
import { useT } from '../../providers/I18nProvider';

const ACTIVITY_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'login', label: 'login' },
  { value: 'login_failed', label: 'login_failed' },
  { value: 'logout', label: 'logout' },
  { value: 'password_change', label: 'password_change' },
  { value: 'password_reset', label: 'password_reset' },
  { value: 'user_signup', label: 'user_signup' },
  { value: '2fa_enabled', label: '2fa_enabled' },
  { value: '2fa_disabled', label: '2fa_disabled' },
  { value: '2fa_verify_failed', label: '2fa_verify_failed' },
  { value: 'security_settings_updated', label: 'security_settings_updated' },
  { value: 'admin_create_user', label: 'admin_create_user' },
  { value: 'admin_deactivate_user', label: 'admin_deactivate_user' },
  { value: 'admin_update_user', label: 'admin_update_user' },
  { value: 'CREATE_ASSESSMENT', label: 'CREATE_ASSESSMENT' },
  { value: 'UPDATE_ASSESSMENT', label: 'UPDATE_ASSESSMENT' },
  { value: 'DELETE_ASSESSMENT', label: 'DELETE_ASSESSMENT' },
  { value: 'CREATE_ORGANIZATION', label: 'CREATE_ORGANIZATION' },
  { value: 'UPDATE_ORGANIZATION', label: 'UPDATE_ORGANIZATION' },
  { value: 'DELETE_ORGANIZATION', label: 'DELETE_ORGANIZATION' },
  { value: 'CREATE_OES', label: 'CREATE_OES' },
  { value: 'UPDATE_OES', label: 'UPDATE_OES' },
  { value: 'DELETE_OES', label: 'DELETE_OES' },
  { value: 'CREATE_INVITE', label: 'CREATE_INVITE' },
  { value: 'DELETE_INVITE', label: 'DELETE_INVITE' },
  { value: 'ACCEPT_INVITE', label: 'ACCEPT_INVITE' },
  { value: 'PASSWORD_RESET_REQUEST', label: 'PASSWORD_RESET_REQUEST' },
  { value: 'PASSWORD_RESET_REQUEST_FAILED', label: 'PASSWORD_RESET_REQUEST_FAILED' },
];

const PAGE_SIZE = 25;

export default function AdminEventViewer() {
  const navigate = useNavigate();
  const t = useT();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    activity_type: '',
    user_id: '',
    date_from: '',
    date_to: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [page, setPage] = useState(0);

  const loadEvents = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (appliedFilters.activity_type) params.activity_type = appliedFilters.activity_type;
    if (appliedFilters.user_id !== '' && appliedFilters.user_id != null) {
      const uid = parseInt(appliedFilters.user_id, 10);
      if (!Number.isNaN(uid)) params.user_id = uid;
    }
    if (appliedFilters.date_from) params.date_from = appliedFilters.date_from;
    if (appliedFilters.date_to) params.date_to = appliedFilters.date_to;
    auditLogsApi.getList(params)
      .then((res) => setEvents(Array.isArray(res.data) ? res.data : []))
      .catch((e) => {
        setError(e.response?.data?.detail || t('admin.eventLoadError'));
        setEvents([]);
      })
      .finally(() => setLoading(false));
  }, [page, appliedFilters]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPage(0);
  };

  const formatEventTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center gap-4">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="text-sm text-eucnet-grey-600 hover:text-eucnet-navy"
          >
            ← {t('nav.admin')}
          </button>
        </div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-eucnet-navy">{t('nav.eventViewer')}</h1>
          <p className="mt-2 text-eucnet-grey-600">
            {t('admin.eventViewerDesc')}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-eucnet-grey-200 p-6 border-l-4 border-l-eucnet-navy">
          <div className="flex flex-wrap gap-3 items-end mb-4">
            <div className="min-w-[200px]">
              <label className="block text-xs font-medium text-eucnet-grey-600 mb-1">{t('admin.eventType')}</label>
              <CustomSelect
                value={filters.activity_type}
                onChange={(e) => setFilters((f) => ({ ...f, activity_type: e.target.value }))}
                options={ACTIVITY_TYPE_OPTIONS}
                placeholder="All"
              />
            </div>
            <div className="w-28">
              <label className="block text-xs font-medium text-eucnet-grey-600 mb-1">{t('admin.eventUserId')}</label>
              <input
                type="text"
                placeholder="—"
                value={filters.user_id}
                onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value }))}
                className="w-full border border-eucnet-grey-300 rounded-lg px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-eucnet-grey-600 mb-1">{t('admin.eventFrom')}</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                className="border border-eucnet-grey-300 rounded-lg px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-eucnet-grey-600 mb-1">{t('admin.eventTo')}</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                className="border border-eucnet-grey-300 rounded-lg px-2 py-1.5 text-sm"
              />
            </div>
            <button
              type="button"
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-eucnet-navy text-white text-sm font-medium rounded-lg hover:bg-eucnet-navy/90"
            >
              {t('admin.eventApply')}
            </button>
          </div>

          {error && (
            <p className="text-sm text-red-600 mb-2">{error}</p>
          )}

          {loading ? (
            <p className="text-sm text-eucnet-grey-500">{t('admin.eventLoading')}</p>
          ) : (
            <>
              <div className="overflow-x-auto border border-eucnet-grey-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-eucnet-grey-100 text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium text-eucnet-grey-700">{t('admin.eventTime')}</th>
                      <th className="px-3 py-2 font-medium text-eucnet-grey-700">{t('admin.eventType')}</th>
                      <th className="px-3 py-2 font-medium text-eucnet-grey-700">{t('admin.eventUser')}</th>
                      <th className="px-3 py-2 font-medium text-eucnet-grey-700">{t('admin.eventIP')}</th>
                      <th className="px-3 py-2 font-medium text-eucnet-grey-700">{t('admin.eventDetails')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-eucnet-grey-200">
                    {events.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-3 py-4 text-eucnet-grey-500 text-center">{t('admin.eventNoMatch')}</td>
                      </tr>
                    ) : (
                      events.map((ev) => (
                        <tr key={ev.id} className="hover:bg-eucnet-grey-50">
                          <td className="px-3 py-2 text-eucnet-grey-700 whitespace-nowrap">{formatEventTime(ev.timestamp)}</td>
                          <td className="px-3 py-2 font-mono text-eucnet-navy">{ev.activity_type}</td>
                          <td className="px-3 py-2">{ev.username ?? (ev.user_id != null ? `#${ev.user_id}` : '—')}</td>
                          <td className="px-3 py-2 text-eucnet-grey-600">{ev.ip_address || '—'}</td>
                          <td className="px-3 py-2 text-eucnet-grey-600 max-w-[200px] truncate" title={ev.details ? JSON.stringify(ev.details) : ''}>
                            {ev.details ? (typeof ev.details === 'object' ? JSON.stringify(ev.details) : String(ev.details)) : '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                  className="text-sm text-eucnet-sky hover:underline disabled:opacity-50 disabled:pointer-events-none"
                >
                  {t('admin.eventPrevious')}
                </button>
                <span className="text-sm text-eucnet-grey-600">{t('admin.eventPage', { page: page + 1 })}</span>
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={events.length < PAGE_SIZE || loading}
                  className="text-sm text-eucnet-sky hover:underline disabled:opacity-50 disabled:pointer-events-none"
                >
                  {t('admin.eventNext')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
